import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { TableBody as Body } from '@mui/material';

import { Badge, TableNoData } from '../../shared/uiComponents';
import { BadgeTypes } from '../../shared/uiComponents/StatusBadge';
import { HeaderProperties, TableDataProperties } from '../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import UserAction from '../../components/UserAction';

import { DispatchProperties } from '../../redux/store';
import { UserProperties } from '../../redux/API/userAPIProperties';
import { getBCBAs } from '../../redux/State/userSlice';
import { getBranchBadgeType } from '../../shared/Helpers/constants';

export interface AdminDataProperties extends TableDataProperties {
	query: UserProperties[] | null;
}

interface RowRendererProperties {
	data: UserProperties[];
}

const getStatusBadge = (numberOfClients: number, isDeactivated: boolean): { label: string; type: BadgeTypes } => {
	if (isDeactivated) return { label: 'Inactive', type: 'gray' };
	if (numberOfClients === 0) return { label: 'No Cases', type: 'warning' };

	return { label: 'Active', type: 'success' };
};

export const Headers: HeaderProperties[] = [
	{ id: '7', name: 'Full Name', orderBy: 'fullName' },
	{
		id: '1',
		name: 'Number of Clients',
		orderBy: 'numberOfClients',
	},
	{ id: '2', name: 'Email Status', orderBy: 'emailConfirmed', width: '150px' },
	{ id: '3', name: 'Status', orderBy: 'isDeactivated', width: '100px' },
	{ id: '4', name: 'Licensed or Limited', orderBy: 'authorizationType.id' },
	{ id: '5', name: 'Role' },
	{ id: '8', name: 'Branch' },
	{ id: '6', name: 'Actions', width: '150px' },
];

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const fetchUsers = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const orderBy = params.get('orderBy') || '';
		const direction = params.get('direction') || '';
		const searchString = params.get('search') || '';

		dispatch(getBCBAs({ page, pageSize, orderBy, direction, searchString }));
	};

	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => {
				const statusBadge = getStatusBadge(row.numberOfClients, row.isDeactivated);

				return (
					<CustomTableRow key={index}>
						<RowTableCell>{row.fullName}</RowTableCell>
						<RowTableCell>{row.numberOfClients}</RowTableCell>
						<RowTableCell>
							<Badge
								label={row.emailConfirmed ? 'Confirmed' : 'Pending'}
								sizeType="sm"
								type={row.emailConfirmed ? 'solidSuccess' : 'solidPrimary'}
							/>
						</RowTableCell>
						<RowTableCell>
							<Badge label={statusBadge.label} sizeType="sm" type={statusBadge.type} />
						</RowTableCell>
						<RowTableCell>{row.authorizationType ? row.authorizationType.name : '-'}</RowTableCell>
						<RowTableCell>{row.role?.name || '-'}</RowTableCell>
						<RowTableCell>
							<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
								{row.branches && row.branches.length > 0 ? (
									row.branches.map((branch) => (
										<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
									))
								) : (
									<span style={{ color: '#999', fontSize: '12px' }}>None</span>
								)}
							</div>
						</RowTableCell>
						<RowTableCell>
							<UserAction item={row} fetchUsers={fetchUsers} />
						</RowTableCell>
					</CustomTableRow>
				);
			})}
		</Body>
	);
};
